import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';

const BankSettings = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ bankName: '', ifscCode: '' });
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const permissions = useMemo(() => getModulePermissions('settings-bank'), []);
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;
  const canManage = canCreate || canEdit;
  const hasActions = canManage || canDelete;
  const isViewOnly = permissions.view && !hasActions;

  const resetForm = () => {
    setFormData({ bankName: '', ifscCode: '' });
    setEditingId(null);
    setFormErrors({});
  };

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiRequest('/api/admin/settings/banks', {}, navigate);
      setBanks(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load banks.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEditing = Boolean(editingId);

    if (isEditing && !canEdit) {
      setError('You do not have permission to edit banks.');
      return;
    }

    if (!isEditing && !canCreate) {
      setError('You do not have permission to create banks.');
      return;
    }

    setError('');
    setSuccessMessage('');
    const trimmedBank = formData.bankName.trim();
    const trimmedIfsc = formData.ifscCode.trim();
    const errors = {};

    if (!trimmedBank) {
      errors.bankName = 'Bank name is required.';
    }

    if (!trimmedIfsc) {
      errors.ifscCode = 'IFSC code is required.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormErrors({});

    const payload = {
      bankName: trimmedBank,
      ifscCode: trimmedIfsc,
    };

    try {
      const endpoint = isEditing
        ? `/api/admin/settings/banks/${editingId}`
        : '/api/admin/settings/banks';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest(
        endpoint,
        { method, body: payload },
        navigate
      );

      setSuccessMessage(
        response.message || (isEditing ? 'Bank updated successfully.' : 'Bank added successfully.')
      );
      resetForm();
      fetchBanks();
    } catch (err) {
      setError(err.message || 'Unable to save bank.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (bank) => {
    if (!canEdit) {
      navigate('/access-denied', { state: { from: '/settings/bank' } });
      return;
    }
    setEditingId(bank.id);
    setFormData({ bankName: bank.bankName || '', ifscCode: bank.ifscCode || '' });
    setSuccessMessage('');
    setError('');
    setFormErrors({});
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      navigate('/access-denied', { state: { from: '/settings/bank' } });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete bank?',
      text: 'Are you sure you want to delete this bank? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionId(id);
    setError('');
    setSuccessMessage('');
    try {
      const response = await apiRequest(
        `/api/admin/settings/banks/${id}`,
        { method: 'DELETE' },
        navigate
      );
      setSuccessMessage(response.message || 'Bank deleted successfully.');
      if (editingId === id) {
        resetForm();
      }
      fetchBanks();
    } catch (err) {
      setError(err.message || 'Unable to delete bank.');
    } finally {
      setActionId(null);
    }
  };

  const isEditing = Boolean(editingId);

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Bank Settings</h4>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate('/settings')}
          >
            Back
          </button>
        </div>

        {isViewOnly && (
          <div className="alert alert-info" role="alert">
            You have view-only access. Create and edit actions are disabled.
          </div>
        )}

        {error && !canManage && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="row">
          {canManage && (
            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-4">{isEditing ? 'Update Bank' : 'Add Bank'}</h5>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  {successMessage && (
                    <div className="alert alert-success" role="alert">
                      {successMessage}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <div className="form-floating">
                        <input
                          type="text"
                          className={`form-control ${formErrors.bankName ? 'is-invalid' : ''}`}
                          id="bankName"
                          placeholder="Enter Bank Name"
                          value={formData.bankName}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, bankName: value }));
                            setFormErrors((prevErrors) => {
                              if (!prevErrors.bankName) {
                                return prevErrors;
                              }
                              const nextErrors = { ...prevErrors };
                              delete nextErrors.bankName;
                              return nextErrors;
                            });
                          }}
                        />
                        <label htmlFor="bankName">Enter Bank Name *</label>
                        {formErrors.bankName && (
                          <div className="invalid-feedback d-block">{formErrors.bankName}</div>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-floating">
                        <input
                          type="text"
                          className={`form-control ${formErrors.ifscCode ? 'is-invalid' : ''}`}
                          id="ifscCode"
                          placeholder="Enter IFSC Code"
                          value={formData.ifscCode}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, ifscCode: value }));
                            setFormErrors((prevErrors) => {
                              if (!prevErrors.ifscCode) {
                                return prevErrors;
                              }
                              const nextErrors = { ...prevErrors };
                              delete nextErrors.ifscCode;
                              return nextErrors;
                            });
                          }}
                        />
                        <label htmlFor="ifscCode">Enter IFSC Code *</label>
                        {formErrors.ifscCode && (
                          <div className="invalid-feedback d-block">{formErrors.ifscCode}</div>
                        )}
                      </div>
                    </div>
                    <button type="submit" className="btn btn-danger" disabled={submitting}>
                      {submitting ? (isEditing ? 'Updating...' : 'Saving...') : isEditing ? 'Update' : 'Save'}
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary ms-2"
                        onClick={resetForm}
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                    )}
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">Existing Banks</h5>
                {loading ? (
                  <p className="text-muted mb-0">Loading banks...</p>
                ) : banks.length === 0 ? (
                  <p className="text-muted mb-0">No banks found.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Bank Name</th>
                          <th>IFSC Code</th>
                          {hasActions && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {banks.map((bank, index) => (
                          <tr key={bank.id}>
                            <td>{index + 1}</td>
                            <td>{bank.bankName}</td>
                            <td>{bank.ifscCode}</td>
                            {hasActions && (
                              <td>
                                {canEdit && (
                                  <button
                                    className="btn btn-sm btn-warning me-2"
                                    onClick={() => handleEdit(bank)}
                                    disabled={submitting || actionId !== null}
                                  >
                                    Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(bank.id)}
                                    disabled={actionId === bank.id || submitting}
                                  >
                                    {actionId === bank.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BankSettings;
